import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { useCompanyBilling } from '../components/CompanyBilling.jsx';
import { clearBillingSelection, readBillingSelection, rememberBillingSelection } from '../billing.js';
import { IconCheck, IconPlay } from '../components/icons.jsx';
import ProtectedVideo from '../components/ProtectedVideo.jsx';
import Beacon, { BEACON_SLOW_TEXT_MEDIA } from '../components/Beacon.jsx';
import {
  catalogGroupsSignature,
  groupPublishedEntries,
  parseRenovationTypeKeys,
  readTenantCatalogInBackground,
  useCuratedCatalogRefresh,
  validRenovationTypeKey,
} from '../curated-catalog.js';

// OGC-531: View all shows the first creatives by configured order and reveals
// the rest inline.
export const COLLAPSED_CREATIVE_COUNT = 2;
const CURATED_MEDIA_MIN_TTL_MS = 5_000;

function tenantHeaders(companyId) {
  return { headers: { 'x-company-id': companyId } };
}

function publishedChoice(entry) {
  const typeKey = entry?.renovationType?.key;
  const pkg = entry?.package;
  const version = entry?.version;
  if (
    !validRenovationTypeKey(typeKey)
    || !entry.renovationType?.id
    || !pkg?.id
    || pkg.lifecycle !== 'published'
    || !pkg.publishedVersionId
    || version?.id !== pkg.publishedVersionId
    || version.status !== 'published'
    || typeof version.primaryText !== 'string'
    || typeof version.headline !== 'string'
  ) return null;
  return entry;
}

// Every eligible Published creative, grouped by its active renovation type in
// the catalog's configured order. A type is a customer offering once it has one
// eligible creative; the campaign step separately limits selection to one or two.
export function normalizePublishedCatalog(value) {
  const entries = (Array.isArray(value) ? value : []).map(publishedChoice).filter(Boolean);
  return groupPublishedEntries(entries, (left, right) => (
    left.version.id === right.version.id || left.package.id === right.package.id
  )).map((group) => Object.freeze({
    ...group,
    choices: Object.freeze(group.choices),
    available: group.choices.length > 0,
  }));
}

function catalogSelection(value, groups) {
  const requested = new Set(Array.isArray(value) ? value : []);
  return (Array.isArray(groups) ? groups : [])
    .filter(({ key }) => requested.has(key))
    .map(({ key }) => key);
}

export function campaignSelectionPath(value) {
  const keys = parseRenovationTypeKeys((Array.isArray(value) ? value : []).join(','));
  return keys.length ? `curated-campaign?types=${keys.join(',')}` : 'curated-campaign';
}

export function parseSelectedRenovationTypes(value) {
  return parseRenovationTypeKeys(value);
}

const EMPTY_CATALOG_GROUPS = Object.freeze([]);

export function tenantBoundCatalogView(currentCompanyId, state = {}) {
  const current = Boolean(currentCompanyId) && state.companyId === currentCompanyId;
  const groups = current && Array.isArray(state.groups) ? state.groups : EMPTY_CATALOG_GROUPS;
  return Object.freeze({
    current,
    status: current ? state.status : currentCompanyId ? 'loading' : 'empty',
    groups,
    selected: current ? catalogSelection(state.selected, groups) : [],
    filter: current && (state.filter === 'all' || groups.some((group) => group.key === state.filter)) ? state.filter : 'all',
    preview: current && state.preview?.companyId === currentCompanyId ? state.preview : null,
  });
}

// Selected types that a refresh removed from the offering, by display name.
export function removedCatalogSelections(selected, previousGroups, nextGroups) {
  const nextKeys = new Set((nextGroups || []).map((group) => group.key));
  return (selected || [])
    .filter((key) => !nextKeys.has(key))
    .map((key) => (previousGroups || []).find((group) => group.key === key)?.displayName || key);
}

export function safePreview(preview, versionId, assetKind) {
  if (
    preview?.versionId !== versionId
    || preview?.assetKind !== assetKind
    || typeof preview?.url !== 'string'
    || !/^https:\/\//i.test(preview.url)
  ) return null;
  return preview.url;
}

export function posterPreviewPath(versionId) {
  return `/api/saas/catalog/versions/${encodeURIComponent(versionId)}/assets/poster/signed-url`;
}

export function safePosterPreview(preview, versionId, now = Date.now()) {
  const url = safePreview(preview, versionId, 'poster');
  const expiresAt = Date.parse(preview?.expiresAt || '');
  if (
    !url
    || typeof preview?.contentType !== 'string'
    || !preview.contentType.toLowerCase().startsWith('image/')
    || !Number.isFinite(expiresAt)
    || expiresAt <= now
  ) return null;
  return url;
}

function samePosterContext(left = {}, right = {}) {
  return Boolean(
    left.companyId
    && left.companyId === right.companyId
    && left.scopeKey === right.scopeKey
    && left.catalogEpoch === right.catalogEpoch
    && left.versionId === right.versionId,
  );
}

export function resolvePosterState(current, requestContext, preview, now = Date.now()) {
  if (!samePosterContext(current, requestContext)) return current;
  const url = safePosterPreview(preview, requestContext.versionId, now);
  return {
    ...requestContext,
    status: url ? 'ready' : 'unavailable',
    url,
  };
}

function exactHttpsUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.hostname);
  } catch {
    return false;
  }
}

export function safeCuratedMediaPreview(
  preview,
  versionId,
  assetKind,
  now = Date.now(),
  minimumTtlMs = 0,
) {
  const expiresAt = Date.parse(preview?.expiresAt || '');
  const contentType = String(preview?.contentType || '').toLowerCase();
  const expectedContentType = assetKind === 'video'
    ? contentType === 'video/mp4'
    : contentType.startsWith('image/');
  if (
    preview?.versionId !== versionId
    || preview?.assetKind !== assetKind
    || !exactHttpsUrl(preview?.url)
    || !expectedContentType
    || !Number.isFinite(expiresAt)
    || expiresAt <= now + minimumTtlMs
  ) return null;
  return preview;
}

function curatedMediaCacheKey(context, assetKind) {
  const { companyId, scopeKey, versionId } = context || {};
  if (!companyId || !scopeKey || !versionId || !assetKind) return '';
  // Catalog versions are immutable, so a descriptor stays valid for its version
  // regardless of catalogEpoch. Keying on the epoch made every options refresh —
  // including a background Meta sync that changed no catalog content — discard
  // usable descriptors and re-request signed URLs for posters already on screen.
  // Tenant and scope stay in the key, and every read still re-validates version,
  // asset kind, URL, content type, and remaining TTL.
  return JSON.stringify([companyId, scopeKey, versionId, assetKind]);
}

export function readCuratedMediaCache(cache, context, assetKind, now = Date.now()) {
  const key = curatedMediaCacheKey(context, assetKind);
  if (!key || !(cache instanceof Map)) return null;
  const preview = cache.get(key);
  const safe = safeCuratedMediaPreview(
    preview,
    context.versionId,
    assetKind,
    now,
    CURATED_MEDIA_MIN_TTL_MS,
  );
  if (!safe) cache.delete(key);
  return safe;
}

export function writeCuratedMediaCache(cache, context, assetKind, preview, now = Date.now()) {
  const key = curatedMediaCacheKey(context, assetKind);
  const safe = key
    ? safeCuratedMediaPreview(
      preview,
      context.versionId,
      assetKind,
      now,
      CURATED_MEDIA_MIN_TTL_MS,
    )
    : null;
  if (!safe || !(cache instanceof Map)) return null;
  cache.set(key, safe);
  return safe;
}

function sameCuratedMediaContext(left = {}, right = {}) {
  return Boolean(
    left.companyId
    && left.companyId === right.companyId
    && left.scopeKey === right.scopeKey
    && left.catalogEpoch === right.catalogEpoch
    && left.versionId === right.versionId
    && left.requestId === right.requestId,
  );
}

export function resolveCuratedMediaState(
  current,
  requestContext,
  preview,
  assetKind,
  now = Date.now(),
) {
  if (!sameCuratedMediaContext(current, requestContext)) return current;
  const descriptor = safeCuratedMediaPreview(
    preview,
    requestContext.versionId,
    assetKind,
    now,
    CURATED_MEDIA_MIN_TTL_MS,
  );
  return {
    ...current,
    status: descriptor ? 'ready' : 'unavailable',
    descriptor,
  };
}

function curatedMediaPath(versionId, assetKind) {
  return `/api/saas/catalog/versions/${encodeURIComponent(versionId)}/assets/${assetKind}/signed-url`;
}

// Poster thumbnails and detail previews can request the same signed descriptor
// concurrently. Keep one resolved and one in-flight store at module scope so a
// thumbnail remount or a detail open can reuse the exact tenant/scope/version
// request. Every read and write still revalidates the immutable version,
// descriptor kind, HTTPS URL, content type, and remaining TTL.
const posterDescriptorCache = new Map();
const pendingPosterDescriptors = new Map();
let posterDescriptorCacheGeneration = 0;

export function resetPosterDescriptorCache() {
  posterDescriptorCacheGeneration += 1;
  posterDescriptorCache.clear();
  pendingPosterDescriptors.clear();
}

export function readPosterDescriptorCache(context, now = Date.now()) {
  return readCuratedMediaCache(posterDescriptorCache, context, 'poster', now);
}

export function writePosterDescriptorCache(context, preview, now = Date.now()) {
  return writeCuratedMediaCache(posterDescriptorCache, context, 'poster', preview, now);
}

export function loadPosterDescriptor(context, loader, now) {
  const cacheReadAt = Number.isFinite(now) ? now : Date.now();
  const cached = readPosterDescriptorCache(context, cacheReadAt);
  if (cached) return Promise.resolve(cached);

  const key = curatedMediaCacheKey(context, 'poster');
  if (!key) return Promise.resolve(null);

  const pending = pendingPosterDescriptors.get(key);
  if (pending) return pending;

  const generation = posterDescriptorCacheGeneration;
  const load = typeof loader === 'function'
    ? loader
    : () => api.get(
      posterPreviewPath(context.versionId),
      tenantHeaders(context.companyId),
    ).then((response) => response?.preview);
  const request = Promise.resolve()
    .then(load)
    .then((preview) => (
      generation === posterDescriptorCacheGeneration
        ? writePosterDescriptorCache(
          context,
          preview,
          Number.isFinite(now) ? now : Date.now(),
        )
        : null
    ))
    .catch(() => null)
    .finally(() => {
      if (pendingPosterDescriptors.get(key) === request) {
        pendingPosterDescriptors.delete(key);
      }
    });
  pendingPosterDescriptors.set(key, request);
  return request;
}

export function useCuratedCreativePreview({
  companyId,
  scopeKey,
  cacheScopeKey = scopeKey,
  catalogEpoch,
}) {
  const [preview, setPreview] = useState(null);
  const requestVersion = useRef(0);
  const previewOpener = useRef(null);
  const mediaCache = useRef(new Map());
  const pendingMedia = useRef(new Map());
  const latestPreview = useRef(null);
  const activeContext = useRef({ companyId, scopeKey, cacheScopeKey, catalogEpoch });
  activeContext.current = { companyId, scopeKey, cacheScopeKey, catalogEpoch };

  // Tenant or scope changes must drop every cached descriptor, because entries
  // are only valid for the company and scope that requested them.
  useEffect(() => {
    requestVersion.current += 1;
    mediaCache.current = new Map();
    pendingMedia.current = new Map();
    previewOpener.current = null;
    setPreview(null);
    return () => { requestVersion.current += 1; };
  }, [companyId, scopeKey, cacheScopeKey]);

  // A catalog refresh may publish new versions, but existing versions are
  // immutable. Cancel in-flight work and close any open preview so nothing binds
  // to a superseded epoch, while keeping still-valid version-keyed descriptors.
  useEffect(() => {
    requestVersion.current += 1;
    pendingMedia.current = new Map();
    previewOpener.current = null;
    setPreview(null);
    return () => { requestVersion.current += 1; };
  }, [catalogEpoch]);

  const currentPreview = preview
    && preview.companyId === companyId
    && preview.scopeKey === scopeKey
    && preview.catalogEpoch === catalogEpoch
    ? preview
    : null;
  latestPreview.current = currentPreview;

  function loadMedia(requestContext, assetKind) {
    const cache = mediaCache.current;
    const pending = pendingMedia.current;
    const cached = readCuratedMediaCache(cache, requestContext, assetKind);
    if (cached) return Promise.resolve(cached);
    const key = curatedMediaCacheKey(requestContext, assetKind);
    if (!key) return Promise.resolve(null);
    if (pending.has(key)) return pending.get(key);
    const request = api.get(
      curatedMediaPath(requestContext.versionId, assetKind),
      tenantHeaders(requestContext.companyId),
    ).then((response) => writeCuratedMediaCache(
      cache,
      requestContext,
      assetKind,
      response?.preview,
    )).catch(() => null).finally(() => {
      if (pending.get(key) === request) pending.delete(key);
    });
    pending.set(key, request);
    return request;
  }

  async function open(entry, opener, mode) {
    const versionId = entry?.version?.id;
    if (!companyId || !scopeKey || !catalogEpoch || !versionId) return;
    previewOpener.current = opener || null;
    const requestId = ++requestVersion.current;
    const requestContext = { companyId, scopeKey, catalogEpoch, versionId, requestId };
    const label = entry?.package?.displayName || entry?.version?.headline || 'Published creative';
    setPreview({
      ...requestContext,
      mode,
      status: 'loading',
      label,
      entry,
      descriptor: null,
      posterDescriptor: null,
    });

    const videoPromise = loadMedia(requestContext, 'video');
    const posterPromise = mode === 'detail'
      ? loadPosterDescriptor({ ...requestContext, scopeKey: cacheScopeKey })
      : Promise.resolve(null);
    const [videoDescriptor, posterDescriptor] = await Promise.all([videoPromise, posterPromise]);
    const active = activeContext.current;
    if (
      requestVersion.current !== requestId
      || active.companyId !== companyId
      || active.scopeKey !== scopeKey
      || active.cacheScopeKey !== cacheScopeKey
      || active.catalogEpoch !== catalogEpoch
    ) return;
    setPreview((current) => {
      const next = resolveCuratedMediaState(current, requestContext, videoDescriptor, 'video');
      if (next === current && !sameCuratedMediaContext(current, requestContext)) return current;
      return { ...next, posterDescriptor };
    });
  }

  function openQuickPlay(entry, opener) {
    return open(entry, opener, 'quick');
  }

  function openDetails(entry, opener) {
    return open(entry, opener, 'detail');
  }

  function closePreview() {
    requestVersion.current += 1;
    setPreview(null);
    const opener = previewOpener.current;
    previewOpener.current = null;
    setTimeout(() => {
      if (opener?.isConnected !== false) opener?.focus?.({ preventScroll: true });
    }, 0);
  }

  function markMediaUnavailable(expectedPreview = latestPreview.current) {
    const current = latestPreview.current;
    if (!current || !sameCuratedMediaContext(current, expectedPreview)) return;
    const key = curatedMediaCacheKey(current, 'video');
    if (key) mediaCache.current.delete(key);
    requestVersion.current += 1;
    setPreview((value) => (
      sameCuratedMediaContext(value, current)
        ? { ...value, status: 'unavailable', descriptor: null }
        : value
    ));
  }

  useEffect(() => {
    if (!currentPreview) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [currentPreview]);

  return {
    preview: currentPreview,
    openQuickPlay,
    openDetails,
    closePreview,
    markMediaUnavailable,
  };
}

export default function CuratedHome({ session, navigate, canCreate = true }) {
  const billing = useCompanyBilling();
  const companyId = session?.user?.activeCompany?.id || session?.user?.tenant?.companyId || '';
  const mediaScopeKey = `${companyId}:home`;
  const [catalogCompanyId, setCatalogCompanyId] = useState('');
  const [catalogEpoch, setCatalogEpoch] = useState(0);
  const [status, setStatus] = useState('loading');
  const [groups, setGroups] = useState(() => normalizePublishedCatalog([]));
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [staleNotice, setStaleNotice] = useState('');
  const requestVersion = useRef(0);
  const catalogSignature = useRef('');
  const loadInFlight = useRef(false);
  const previewOpen = useRef(false);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const media = useCuratedCreativePreview({
    companyId,
    scopeKey: mediaScopeKey,
    cacheScopeKey: mediaScopeKey,
    catalogEpoch,
  });
  previewOpen.current = Boolean(media.preview);

  // Entry loads replace the view; background refreshes change it only when the
  // customer-visible catalog changed, and keep the current view on failure.
  const loadCatalog = useCallback((background) => {
    if (!companyId) return;
    // A background signal never supersedes the read already in flight (if it
    // did and then failed, the entry load would never settle), and an open
    // preview defers it so a playing video is never closed under the viewer.
    if (background && (loadInFlight.current || previewOpen.current)) return;
    const version = ++requestVersion.current;
    loadInFlight.current = true;
    if (!background) setStatus('loading');
    (background
      ? readTenantCatalogInBackground(api, tenantHeaders(companyId))
      : api.get('/api/saas/catalog', tenantHeaders(companyId)))
      .then((response) => {
        if (version !== requestVersion.current) return;
        if (background && previewOpen.current) return;
        const nextGroups = normalizePublishedCatalog(response?.catalog);
        const signature = catalogGroupsSignature(nextGroups);
        if (background && signature === catalogSignature.current) return;
        catalogSignature.current = signature;
        const previousGroups = groupsRef.current;
        setCatalogEpoch((epoch) => epoch + 1);
        setGroups(nextGroups);
        setStatus(nextGroups.length ? 'ready' : 'empty');
        const removed = removedCatalogSelections(selectedRef.current, previousGroups, nextGroups);
        if (removed.length) {
          setStaleNotice(`${removed.join(', ')} ${removed.length === 1 ? 'is' : 'are'} no longer available and ${removed.length === 1 ? 'was' : 'were'} removed from your selection.`);
        }
        setSelected((current) => catalogSelection(current, nextGroups));
      })
      .catch(() => {
        if (version !== requestVersion.current || background) return;
        catalogSignature.current = '';
        setGroups(normalizePublishedCatalog([]));
        setStatus('unavailable');
      })
      .finally(() => {
        if (version === requestVersion.current) loadInFlight.current = false;
      });
  }, [companyId]);

  useEffect(() => {
    requestVersion.current += 1;
    loadInFlight.current = false;
    catalogSignature.current = '';
    setCatalogEpoch((epoch) => epoch + 1);
    setSelected(readBillingSelection(companyId));
    setFilter('all');
    setExpanded(new Set());
    setStaleNotice('');
    setCatalogCompanyId(companyId);
    if (!companyId) {
      setGroups(normalizePublishedCatalog([]));
      setStatus('empty');
      return undefined;
    }
    loadCatalog(false);
    return () => { requestVersion.current += 1; };
  }, [companyId, loadCatalog]);

  useCuratedCatalogRefresh(() => loadCatalog(true), { enabled: Boolean(companyId) });

  const view = tenantBoundCatalogView(companyId, {
    companyId: catalogCompanyId,
    status,
    groups,
    selected,
    filter,
    preview: media.preview,
  });

  const visibleGroups = useMemo(
    () => view.filter === 'all' ? view.groups : view.groups.filter((group) => group.key === view.filter),
    [view.filter, view.groups],
  );
  const selectedSet = new Set(view.selected);

  useEffect(() => {
    if (view.current && view.status === 'ready' && canCreate) rememberBillingSelection(companyId, view.selected);
  }, [companyId, canCreate, view.current, view.status, view.selected.join(',')]);

  function toggleType(typeKey) {
    const group = view.groups.find((candidate) => candidate.key === typeKey);
    if (!canCreate || !group?.available) return;
    setStaleNotice('');
    setSelected((current) => (
      current.includes(typeKey)
        ? current.filter((key) => key !== typeKey)
        : catalogSelection([...current, typeKey], view.groups)
    ));
  }

  async function continueToSetup() {
    if (!canCreate || !view.selected.length) return;
    rememberBillingSelection(companyId, view.selected);
    if (!await billing.requestAccess('create')) return;
    clearBillingSelection(companyId);
    navigate?.(campaignSelectionPath(view.selected));
  }

  function toggleExpanded(typeKey, button) {
    const collapsing = expanded.has(typeKey);
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(typeKey)) next.delete(typeKey); else next.add(typeKey);
      return next;
    });
    // Anchor after the collapse commits, so the button stays in view.
    if (collapsing) requestAnimationFrame(() => button?.scrollIntoView?.({ block: 'nearest' }));
  }

  return (
    <div className="bl-home">
      <header className="bl-home-hero">
        <div>
          <div className="bl-kicker">Published ad catalog</div>
          <h1>Launch with creative that is ready to run.</h1>
          <p>
            Choose one or more renovation types. Preview the available ads, then make your selection in campaign setup.
          </p>
        </div>
        <div className="bl-hero-summary" aria-live="polite">
          <strong>{view.selected.length}</strong>
          <span>{view.selected.length === 1 ? 'renovation type selected' : 'renovation types selected'}</span>
          <small>Choose 1–2 ads per type next.</small>
        </div>
      </header>

      <section aria-labelledby="catalog-heading" className="bl-catalog-section">
        <div className="bl-section-heading">
          <div>
            <div className="bl-kicker">Curated for Meta</div>
            <h2 id="catalog-heading">Published renovation ads</h2>
          </div>
          <div className="bl-filter-list" aria-label="Filter renovation types">
            <button type="button" aria-pressed={view.filter === 'all'} onClick={() => setFilter('all')}>All types</button>
            {view.groups.map((group) => (
              <button
                type="button"
                key={group.key}
                aria-pressed={view.filter === group.key}
                onClick={() => setFilter(group.key)}
              >
                {group.displayName}
              </button>
            ))}
          </div>
        </div>

        {staleNotice && view.status === 'ready' && <CatalogNotice compact role="status" title="Your selection changed" body={staleNotice} />}
        {view.status === 'loading' && <CatalogNotice kind="loading" title="Loading Published ads" body="Checking the approved catalog for this company." />}
        {view.status === 'empty' && <CatalogNotice title="No Published ads are available" body="Contact your BeyondLeads operator to publish the curated renovation catalog." />}
        {view.status === 'unavailable' && <CatalogNotice title="Ad catalog is temporarily unavailable" body="Try again shortly or contact your BeyondLeads operator for help." />}

        {view.status === 'ready' && (
          <div className="bl-catalog-groups">
            {visibleGroups.map((group) => {
              const total = group.choices.length;
              const isExpanded = expanded.has(group.key);
              const collapsible = total > COLLAPSED_CREATIVE_COUNT;
              const visibleCount = collapsible && !isExpanded ? COLLAPSED_CREATIVE_COUNT : total;
              const gridId = `home-creatives-${group.key}`;
              return (
                <article
                  className={`bl-catalog-group${selectedSet.has(group.key) ? ' is-selected' : ''}`}
                  key={group.key}
                  data-renovation-type={group.key}
                >
                  <div className="bl-group-heading">
                    <div>
                      <span className="bl-published-pill">Published</span>
                      <h3>{group.displayName}</h3>
                      <p>{total} {total === 1 ? 'creative' : 'creatives'} available</p>
                    </div>
                    {canCreate && (
                      <label className="bl-type-selector">
                        <input
                          type="checkbox"
                          checked={selectedSet.has(group.key)}
                          onChange={() => toggleType(group.key)}
                        />
                        <span><IconCheck size={15} /> Include {group.displayName}</span>
                      </label>
                    )}
                  </div>

                  <div className={`bl-choice-grid${total === 1 ? ' is-single' : ''}`} id={gridId}>
                    {group.choices.map((entry, index) => (
                      <CreativeCard
                        key={`${companyId}:${catalogEpoch}:${entry.version.id}`}
                        companyId={companyId}
                        mediaScopeKey={mediaScopeKey}
                        catalogEpoch={catalogEpoch}
                        entry={entry}
                        index={index}
                        hidden={index >= visibleCount}
                        onPlay={media.openQuickPlay}
                        onPreview={media.openDetails}
                      />
                    ))}
                  </div>
                  {collapsible && (
                    <div className="bl-group-browse-bar">
                      <p>{visibleCount} of {total} creatives</p>
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={gridId}
                        onClick={(event) => toggleExpanded(group.key, event.currentTarget)}
                      >
                        {isExpanded ? 'Show fewer' : `View all ${total} creatives`} <span aria-hidden="true">{isExpanded ? '−' : '+'}</span>
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {canCreate && view.status === 'ready' && (
        <div className="bl-selection-bar">
          <div>
            <strong>{view.selected.length ? `${view.selected.length} renovation type${view.selected.length === 1 ? '' : 's'} selected` : 'Choose a renovation type to get started.'}</strong>
            <span>Choose 1–2 ads for each selected type in campaign setup.</span>
          </div>
          <button
            type="button"
            disabled={!view.selected.length}
            onClick={continueToSetup}
          >
            Continue to campaign setup
          </button>
        </div>
      )}

      {!canCreate && view.status === 'ready' && (
        <CatalogNotice compact title="Catalog view only" body="You can preview Published ads. Ask a company administrator for campaign creation access." />
      )}

      {view.preview?.mode === 'quick' && (
        <QuickPlayDialog
          preview={view.preview}
          onClose={media.closePreview}
          onMediaError={media.markMediaUnavailable}
        />
      )}
      {view.preview?.mode === 'detail' && (
        <PreviewDialog
          preview={view.preview}
          onClose={media.closePreview}
          onMediaError={media.markMediaUnavailable}
        />
      )}
    </div>
  );
}

function CreativeCard({
  companyId,
  mediaScopeKey,
  catalogEpoch,
  entry,
  index,
  hidden = false,
  onPlay,
  onPreview,
}) {
  const title = entry.package.displayName || `Creative ${index + 1}`;
  return (
    <article className="bl-creative-card" hidden={hidden}>
      <PosterThumbnail
        companyId={companyId}
        scopeKey={mediaScopeKey}
        cacheScopeKey={mediaScopeKey}
        catalogEpoch={catalogEpoch}
        versionId={entry.version.id}
        index={index}
        creativeName={title}
        onPlay={(opener) => onPlay(entry, opener)}
      />
      <div className="bl-creative-content">
        <div className="bl-creative-meta">Approved choice {index + 1}</div>
        <h4>{title}</h4>
        <strong>{entry.version.headline}</strong>
        <p>{entry.version.primaryText}</p>
        <button type="button" onClick={(event) => onPreview(entry, event.currentTarget)}>
          <IconPlay size={14} /> Preview video and copy
        </button>
      </div>
    </article>
  );
}

export function PosterThumbnail({
  companyId,
  scopeKey = '',
  // Descriptor cache scope. Callers whose scopeKey encodes transient UI state —
  // the curated setup screen folds the current renovation-type selection into it —
  // must pass a scope that stays stable across that state, or a poster already on
  // screen is re-requested when the selection changes. Defaults to scopeKey for
  // callers whose scope is already stable, such as Home.
  cacheScopeKey = scopeKey,
  catalogEpoch,
  versionId,
  index,
  creativeName = `Video ${index + 1}`,
  onPlay,
  className = '',
}) {
  const containerRef = useRef(null);
  const [poster, setPoster] = useState(() => ({
    companyId,
    scopeKey,
    catalogEpoch,
    versionId,
    status: 'idle',
    url: null,
  }));
  const context = { companyId, scopeKey, catalogEpoch, versionId };
  const posterUrl = samePosterContext(poster, context) && poster.status === 'ready'
    ? poster.url
    : null;
  const posterStatus = samePosterContext(poster, context) ? poster.status : 'idle';

  useEffect(() => {
    let active = true;
    let requested = false;
    let observer = null;
    const requestContext = { companyId, scopeKey, catalogEpoch, versionId };
    // Cache identity is deliberately separate from render identity. The render
    // context keeps scopeKey so stale responses are still discarded, while the
    // cache context uses a scope that survives transient UI state and omits
    // catalogEpoch, because catalog versions are immutable.
    const cacheContext = { companyId, scopeKey: cacheScopeKey, catalogEpoch, versionId };
    setPoster({ ...requestContext, status: 'idle', url: null });
    if (!companyId || !catalogEpoch || !versionId) return undefined;

    const loadPoster = () => {
      if (!active || requested) return;
      requested = true;
      // A descriptor already resolved for this exact version is reused instead of
      // re-requesting a signed URL. The thumbnail is remounted whenever scopeKey or
      // catalogEpoch changes, which resets the per-run `requested` flag, so without
      // this the fresh observer re-fetches posters that are already displayed.
      setPoster({ ...requestContext, status: 'loading', url: null });
      loadPosterDescriptor(cacheContext)
        .then((preview) => {
          if (!active) return;
          setPoster((current) => resolvePosterState(current, requestContext, preview));
        });
    };

    const target = containerRef.current;
    if (target && typeof globalThis.IntersectionObserver === 'function') {
      observer = new globalThis.IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer?.disconnect();
        loadPoster();
      }, { rootMargin: '240px 0px', threshold: 0.01 });
      observer.observe(target);
    } else {
      loadPoster();
    }

    return () => {
      active = false;
      observer?.disconnect();
    };
  }, [companyId, scopeKey, cacheScopeKey, catalogEpoch, versionId]);

  const handlePosterError = () => {
    setPoster((current) => samePosterContext(current, context)
      ? { ...context, status: 'unavailable', url: null }
      : current);
  };

  return (
    <button
      type="button"
      ref={containerRef}
      className={[
        'bl-video-placeholder',
        className,
        posterUrl ? 'has-poster' : '',
      ].filter(Boolean).join(' ')}
      data-poster-state={posterStatus}
      aria-label={`Play ${creativeName}`}
      onClick={(event) => onPlay?.(event.currentTarget)}
    >
      {posterUrl && (
        <img
          className="bl-video-poster"
          src={posterUrl}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          draggable="false"
          onError={handlePosterError}
        />
      )}
      <span aria-hidden="true"><IconPlay size={24} /></span>
    </button>
  );
}

function CatalogNotice({ title, body, compact = false, kind = 'neutral', role }) {
  const loading = kind === 'loading';
  return (
    <div
      className={`bl-catalog-notice${compact ? ' is-compact' : ''}`}
      role={role || (loading ? 'status' : undefined)}
      aria-live={loading ? 'polite' : undefined}
      aria-busy={loading ? 'true' : undefined}
    >
      {loading && <Beacon size={compact ? 'control' : 'page'} delayMs={0} />}
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function containPreviewFocus(event) {
  if (event.key !== 'Tab') return;
  const controls = [...event.currentTarget.querySelectorAll(
    'button:not(:disabled), video[controls], a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => element.getClientRects().length > 0);
  if (!controls.length) return;
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function usePreviewCloseFocus(status) {
  const closeButtonRef = useRef(null);
  useEffect(() => {
    closeButtonRef.current?.focus?.({ preventScroll: true });
  }, []);
  useEffect(() => {
    if (status === 'unavailable') {
      closeButtonRef.current?.focus?.({ preventScroll: true });
    }
  }, [status]);
  return closeButtonRef;
}

export function QuickPlayDialog({ preview, onClose, onMediaError }) {
  const closeButtonRef = usePreviewCloseFocus(preview.status);
  const label = `Play ${preview.label}`;
  return (
    <div
      className="bl-preview-backdrop bl-quick-play-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        className="bl-quick-play-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-preview-mode="playback"
        onKeyDown={containPreviewFocus}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="bl-quick-play-close"
          aria-label={`Close ${preview.label} video`}
          onClick={onClose}
        >
          ×
        </button>
        {preview.status === 'loading' && (
          <div className="bl-quick-play-status" aria-busy="true">
            <Beacon size="control" label="Preparing video…" delayMs={0} slowText={BEACON_SLOW_TEXT_MEDIA} />
          </div>
        )}
        {preview.status === 'unavailable' && (
          <div className="bl-quick-play-status" role="status">Video temporarily unavailable.</div>
        )}
        {preview.status === 'ready' && preview.descriptor?.url && (
          <ProtectedVideo
            className="bl-quick-play-video"
            controls
            autoPlay
            playsInline
            preload="metadata"
            aria-label={`${preview.label} video`}
            src={preview.descriptor.url}
            onError={() => onMediaError?.(preview)}
          >
            Your browser does not support video playback.
          </ProtectedVideo>
        )}
      </section>
    </div>
  );
}

function PreviewDialog({ preview, onClose, onMediaError }) {
  const title = preview.entry.package.displayName || preview.entry.version.headline;
  const closeButtonRef = usePreviewCloseFocus(preview.status);
  return (
    <div className="bl-preview-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target) onClose();
    }}>
      <section className="bl-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="bl-preview-title" data-preview-mode="details" onKeyDown={containPreviewFocus}>
        <div className="bl-preview-heading">
          <div>
            <div className="bl-kicker">Published creative</div>
            <h2 id="bl-preview-title">{title}</h2>
          </div>
          <button ref={closeButtonRef} type="button" aria-label="Close creative preview" onClick={onClose}>×</button>
        </div>
        {preview.status === 'loading' && <CatalogNotice kind="loading" compact title="Loading private preview" body="Preparing a short-lived video link." />}
        {preview.status === 'unavailable' && <CatalogNotice compact role="status" title="Preview unavailable" body="The approved copy is still shown below. Try the video again later or contact your operator." />}
        {preview.status === 'ready' && (
          <ProtectedVideo className="bl-preview-video" controls preload="metadata" poster={preview.posterDescriptor?.url || undefined} src={preview.descriptor?.url} onError={() => onMediaError?.(preview)}>
            Your browser does not support video playback.
          </ProtectedVideo>
        )}
        <div className="bl-preview-copy">
          <div>
            <span>Headline</span>
            <strong>{preview.entry.version.headline}</strong>
          </div>
          <div>
            <span>Primary text</span>
            <p>{preview.entry.version.primaryText}</p>
          </div>
          <div>
            <span>Call to action</span>
            <strong>{String(preview.entry.version.callToAction || 'LEARN_MORE').replaceAll('_', ' ')}</strong>
          </div>
        </div>
      </section>
    </div>
  );
}
